// Mobile API — typed wrappers pro /mobile/* endpointy.

import type { ApiClient } from "./client";
import type {
  AuthResponse,
  CollectionExpected,
  TrankToday,
  TrankMaterial,
  DriverBrief,
  FuelingIn,
  FuelingOut,
  RouteForToday,
  Shift,
  ShiftStartIn,
  ShiftEndIn,
  StopEventIn,
  Vehicle,
} from "../types";

export type LamaRarity = "c" | "r" | "e" | "l";

export interface LamaCard {
  id: number;
  nm: string;
  rarity: LamaRarity;
  hlaska: string;
  img: string;
}

export interface LamyOverview {
  /** Server-side feature flag — dokud je false, appka vstup vůbec neukazuje. */
  enabled: boolean;
  km_total: number;
  packs_available: number;
  km_to_next: number;
  packs_opened: number;
  km_per_pack: number;
  set: LamaCard[];
  inventar: Record<string, number>;
  leaderboard: Array<{ name: string; col: number; me: boolean }>;
}

export interface LamyOpenResult {
  cards: Array<LamaCard & { new: boolean }>;
  km_total: number;
  packs_available: number;
  km_to_next: number;
  packs_opened: number;
}

export function createMobileApi(client: ApiClient) {
  return {
    auth: {
      login: (pin: string): Promise<AuthResponse> =>
        client.post<AuthResponse>("/mobile/auth", { pin }),
      whoami: (): Promise<DriverBrief> => client.get<DriverBrief>("/mobile/me"),
    },

    /** Mobilka pri startu app — pro picker vozidla. JWT auth, scoped by driver workspace. */
    vehicles: {
      list: (): Promise<Vehicle[]> => client.get<Vehicle[]>("/mobile/vehicles"),
    },

    route: {
      today: (): Promise<RouteForToday[]> =>
        client.get<RouteForToday[]>("/mobile/route/today"),
      // Bezpečnostní gate: activated = dispečer odemkl kurzy; has_assignment = řidič
      // má dnes přiřazenou trasu. has_assignment && !activated → „Čeká na aktivaci".
      status: (): Promise<{ date: string; activated: boolean; has_assignment: boolean }> =>
        client.get("/mobile/route/status"),
      /** Silniční úsek odkud→kam pro detail zastávky (OSRM): geometry =
       *  GeoJSON LineString ([lng,lat]), min = minuty jízdy, km = vzdálenost.
       *  Jeden zdroj pro metriky i mapu. */
      line: (fromLat: number, fromLng: number, toLat: number, toLng: number): Promise<{
        geometry: { type: "LineString"; coordinates: [number, number][] } | null;
        min: number | null;
        km: number | null;
      }> =>
        client.get(`/mobile/route-line?from_lat=${fromLat}&from_lng=${fromLng}&to_lat=${toLat}&to_lng=${toLng}`),
    },

    /** Lama Klub — odměnový systém. Km počítá server z Commander GPS,
     *  tah balíčku se losuje server-side (žádné podvádění na klientovi). */
    lamy: {
      overview: (): Promise<LamyOverview> => client.get("/mobile/lamy/overview"),
      open: (): Promise<LamyOpenResult> => client.post("/mobile/lamy/open", {}),
    },

    // Materiál řidiče jede přes `/mobile/zasilky/*` (viz packages/mobile/src/lib/api.ts).
    // Zdejší klient pro starý `/mobile/material/today` + `/confirm` byl zrušen
    // 2026-08-16 spolu s endpointy — nechat ho tu by znamenalo nabízet cestu, která
    // vrací 404.

    shift: {
      active: (): Promise<Shift | null> => client.get<Shift | null>("/mobile/shift/active"),
      start: (body: ShiftStartIn): Promise<Shift> =>
        client.post<Shift>("/mobile/shift/start", body),
      end: (shiftId: string, body?: ShiftEndIn): Promise<Shift> =>
        client.post<Shift>(`/mobile/shift/${shiftId}/end`, body ?? {}),
      pause: (paused: boolean): Promise<void> =>
        client.post<void>("/mobile/shift/pause", { paused }),

      event: (shiftId: string, body: StopEventIn): Promise<{ id: string; deduplicated: boolean }> =>
        client.post<{ id: string; deduplicated: boolean }>(
          `/mobile/shift/${shiftId}/event`,
          body,
        ),

      /** Hydrate progress: vrati chronologicky vsechny stop events smeny. */
      events: (shiftId: string): Promise<Array<{
        id: string;
        stop_id: string;
        event_type: string;
        ts: string;
        payload?: { count?: number; reason?: string } | null;
      }>> => client.get(`/mobile/shift/${shiftId}/events`),

      /** Krok zpět — smaže poslední status event zastávky (řidič se překlikl). */
      undoLast: (shiftId: string, stopId: string): Promise<void> =>
        client.delete(`/mobile/shift/${shiftId}/event/last?stop_id=${encodeURIComponent(stopId)}`),

      /** Bulk sync up po obnove pripojeni (Sprint 4). */
      eventsBatch: (entries: Array<{ shift_id: string; event: StopEventIn }>):
        Promise<Array<{ id: string; status: "inserted" | "deduplicated" | "rejected"; error?: string }>> =>
        client.post("/mobile/events/batch", { events: entries }),

      /** Sprint 6: GPS positions bulk upload. */
      gpsBatch: (positions: Array<{ id: string; shift_id: string; ts: string; lat: number; lng: number; accuracy?: number | null; battery?: number | null }>):
        Promise<Array<{ id: string; status: "inserted" | "deduplicated" | "rejected" }>> =>
        client.post("/mobile/gps/batch", { positions }),

      /** Uloží pečeti bedny k aktivní směně. */
      addBoxSeals: (seals: string[]): Promise<void> =>
        client.post<void>("/mobile/shift/active/box-seals", { seals }),

      /** TRANK — dnešní nakládací zastávky pro přihlášeného řidiče. */
      trankToday: (): Promise<TrankToday[]> =>
        client.get<TrankToday[]>("/mobile/trank/today"),

      /** TRANK — očekávané pečeti ze zdrojových směn (nakládka). */
      collectionExpected: (collectionId: string): Promise<CollectionExpected> =>
        client.get<CollectionExpected>(`/mobile/collection/${encodeURIComponent(collectionId)}/expected`),

      /** TRANK — uloží výsledek nakládky (naskenované pečeti). */
      collectionComplete: (collectionId: string, shiftId: string, scanned: string[]): Promise<void> =>
        client.post<void>(`/mobile/collection/${encodeURIComponent(collectionId)}/complete`, { shift_id: shiftId, scanned }),

      /** TRANK — schová ohlášenou pečeť (je jen informační; vratné přes unhide). */
      collectionSealHide: (collectionId: string, kod: string): Promise<void> =>
        client.post<void>(`/mobile/collection/${encodeURIComponent(collectionId)}/seal-hide`, { kod }),

      /** TRANK — vrátí schovanou ohlášenou pečeť zpátky. */
      collectionSealUnhide: (collectionId: string, kod: string): Promise<void> =>
        client.delete(`/mobile/collection/${encodeURIComponent(collectionId)}/seal-hide?kod=${encodeURIComponent(kod)}`),

      /** TRANK — potvrdí vyložení v Brně. */
      unloadingComplete: (collectionId: string, shiftId: string): Promise<void> =>
        client.post<void>(`/mobile/collection/${encodeURIComponent(collectionId)}/unloading-complete`, { shift_id: shiftId }),

      /** TRANK — materiál kurzu: co má řidič naložit v Brně a co už veze. */
      trankMaterial: (collectionId: string): Promise<TrankMaterial> =>
        client.get<TrankMaterial>(`/mobile/collection/${encodeURIComponent(collectionId)}/material`),

      /** TRANK — řidič naložil materiál v Brně (sken pečetí). */
      trankMaterialNalozeno: (collectionId: string, zasilky: string[]): Promise<{ nalozeno: number }> =>
        client.post<{ nalozeno: number }>(
          `/mobile/collection/${encodeURIComponent(collectionId)}/material-nalozeno`, { zasilky }),

      /** TRANK — složil materiál na cílovém depu. `chybi` = kusy, které v bedně nebyly
       *  (dostanou stav „Pohřešuje se"). Tohle nahrazuje celý prijem.routera.cz. */
      trankMaterialVylozeno: (collectionId: string, zasilky: string[], chybi: string[]):
        Promise<{ prevzato: number; pohresuje_se: number }> =>
        client.post<{ prevzato: number; pohresuje_se: number }>(
          `/mobile/collection/${encodeURIComponent(collectionId)}/material-vylozeno`, { zasilky, chybi }),

      /** Zaznamenat tankování ke smene. */
      fueling: (shiftId: string, body: FuelingIn): Promise<FuelingOut> =>
        client.post<FuelingOut>(`/mobile/shift/${shiftId}/fueling`, body),

      /** Vsechna tankování smeny. */
      fuelings: (shiftId: string): Promise<FuelingOut[]> =>
        client.get<FuelingOut[]>(`/mobile/shift/${shiftId}/fuelings`),

      /** Photo upload — primy fetch s FormData (klient nepodporuje multipart genericky). */
      uploadPhoto: async (
        baseUrl: string,
        token: string,
        shiftId: string,
        photoType: "pre_front" | "pre_back" | "post_front" | "post_back",
        file: Blob,
        filename: string,
      ): Promise<{ path: string; bytes: number }> => {
        const fd = new FormData();
        fd.append("photo_type", photoType);
        fd.append("file", file, filename);
        const res = await fetch(`${baseUrl}/mobile/shift/${shiftId}/photo`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: fd,
        });
        if (!res.ok) {
          const detail = await res.text();
          throw new Error(`Photo upload failed (${res.status}): ${detail}`);
        }
        return res.json();
      },
    },
  };
}

export type MobileApi = ReturnType<typeof createMobileApi>;
